import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Stack, TextField, Typography, Tooltip, Checkbox, InputAdornment } from '@mui/material';
import Restore from '@mui/icons-material/Restore';
import CloseIcon from '@mui/icons-material/Close';
import { ContentCopy, FileDownload, LocalShipping, ReceiptLong } from '@mui/icons-material';
import CurrencyInput from 'react-currency-input-field';
import axiosInstance from '../../utils/axios';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AGGridPictureCol from '../../reuseable-components/AGGrid/AGGridPictureCol';
import { objectToFormData } from '../../utils/objectToFormData';
import AGGridHeaderCheckboxSimple from '../../reuseable-components/AGGrid/AGGridHeaderCheckboxSimple';
import ConfirmDialog from '../../reuseable-components/ConfirmDialog/ConfirmDialog';
import autoSizeColumns from '../../reuseable-components/AGGrid/AGGridAutoSizeCols';
import OrderFiles from './OrderFiles';
import { UserContext } from '../../context/UserProvider';
import { copyToClipboard } from '../../utils/copyToClipboard';
import updateItemInPlaceOrAppend from '../../utils/updateItemInPlaceOrAppend';


EditOrderForm.propTypes = {
  editOrder: PropTypes.object,
  open: PropTypes.bool,
  close: PropTypes.func,
  setOrders: PropTypes.func,
}

export default function EditOrderForm({ editOrder, setOrders, open, close }) {
  const gridRef = useRef(null);
  const { isInRole } = useContext(UserContext);

  const [orderItems, setOrderItems] = useState([]);
  const [originalOrderItems, setOriginalOrderItems] = useState([]);
  const [facility, setFacility] = useState(null);
  const [facilityOnHold, setFacilityOnHold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendOrder, setResendOrder] = useState(false);
  const [resendOrderAsNewOrder, setResendOrderAsNewOrder] = useState(false);
  const [resendWithConfirmationEmail, setResendWithConfirmationEmail] = useState(false);
  const [disableReorderButton, setDisableReorderButton] = useState(true);
  const [openOrderFiles, setOpenOrderFiles] = useState(false);
  const [restrictCustomerInvoices, setRestrictCustomerInvoices] = useState(false);
  const [submittingRestrictCustomerInvoices, setSubmittingRestrictCustomerInvoices] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      axiosInstance.get(`/order/orderData?id=${editOrder.id}`)
        .then(res => {
          setOrderItems(res.data.items ?? []);
          setOriginalOrderItems(JSON.parse(JSON.stringify(res.data.items ?? [])));
          setFacility(res.data.facilityName);
          setFacilityOnHold(res.data.facilityOnHold);
          setRestrictCustomerInvoices(res.data.restrictCustomerInvoices);
        })
        .catch(() => enqueueSnackbar('Order data fetch failed', { variant: 'error' }))
        .finally(() => setLoading(false));
    }
    else {
      setLoading(true);
      setOrderItems([]);
      setFacility(null);
      setResendOrder(false);
      setResendOrderAsNewOrder(false);
      setRestrictCustomerInvoices(false);
    }
  }, [open]);

  const OriginalValueToolTip = (specialItem, newValueObject, newValue, originalValue, isOriginalCartPricing = true, column) =>
    <Box display="inline-flex" component="span" alignItems="center">
      {newValue !== originalValue && !specialItem &&
        <Tooltip title={resendOrder ? `Ordered value: ${originalValue}` : !newValueObject ? "UOM/Item was deleted" : !isOriginalCartPricing ? `Edited in cart, current value: ${newValue}` : `Current value: ${newValue}`} arrow placement="top">
          <IconButton edge="end" color="secondary" disableRipple sx={{ px: 0, my: 0, mr: 1 }}>
            <Restore />
          </IconButton>
        </Tooltip>}
      {resendOrder && (column === "price" || column === "vendorPrice") ? null : originalValue}
    </Box>;

  const updateRow = (params, field, newValue) => {
    const newNodeData = { ...params.node.data };
    if (field === "price" || field === "vendorPrice") newNodeData.newData[field] = newValue
    else newNodeData[field] = newValue
    params.node.setData(newNodeData);
    if (field === "resend") params.api.refreshHeader();
  }

  const updateOrderItems = (params, field, value) => {
    setOrderItems(orderItems => {
      const oldItem = orderItems.find(item => item.id === params.data.id);
      if (!oldItem) return orderItems;
      if (field === "price" || field === "vendorPrice") oldItem.newData[field] = Number(value).toFixed(2);
      else oldItem[field] = value;
      setDisableReorderButton(!orderItems.some(x => (x.resend && x.quantity)))
      return orderItems;
    });
  }

  const updateAllRowResends = (params, resend) => {
    params.api.forEachNodeAfterFilter(node => { node.data.resend = !resend; });
    setOrderItems(orderItems => {
      orderItems.forEach(prod => { prod.resend = !resend; });
      setDisableReorderButton(!orderItems.some(x => (x.resend && x.quantity)))
      return orderItems;
    });
    params.api.refreshCells();
    params.api.refreshHeader();
  }

  const handleRestrictCustomerInvoices = (value) => {
    setSubmittingRestrictCustomerInvoices(true);
    axiosInstance.post(`/order/updateOrderRestrictCustomerInvoices`, objectToFormData({
      restrictCustomerInvoices: value,
      orderId: editOrder.id
    }))
      .then(() => {
        setRestrictCustomerInvoices(value);
        enqueueSnackbar('Order restrict customer invoices updated', { variant: 'success' });
      })
      .catch(() => enqueueSnackbar('Order restrict customer invoices failed', { variant: 'error' }))
      .finally(() => setSubmittingRestrictCustomerInvoices(false));
  }

  const resubmitOrder = () => axiosInstance.post(`/order/resendOrder`, objectToFormData({
    orderId: editOrder.id,
    resendOrderAsNewOrder,
    resendWithConfirmationEmail,
    items: orderItems.filter(item => item.resend).map(item => ({
      price: item.newData?.price ?? item.oldData.price,
      vendorPrice: item.newData?.vendorPrice ?? item.oldData.vendorPrice,
      id: item.id,
      quantity: item.quantity,
      vendorItemId: item.newData?.vendorItemId ?? item.oldData.vendorItemId,
      uomType: item.newData?.uomType ?? item.oldData.uomType,
      productUOMId: item.productUOMId,
      productId: item.productId,
    }))
  }))
    .then(res => {
      if (res.data) {
        setOrders(orders => updateItemInPlaceOrAppend(orders, res.data));
        close();
        enqueueSnackbar('Order resent', { variant: 'success' })
      } else { enqueueSnackbar('Order resend failed', { variant: 'error' }) }
    })
    .catch(() => enqueueSnackbar('Order resend failed', { variant: 'error' }));

  const columns = useMemo(() => [
    AGGridPictureCol(),
    {
      field: 'productName',
      minWidth: 150,
      cellRenderer: (params) => OriginalValueToolTip(params.data.productId < 0, params.data.newData, params.data.newData?.productName, params.data.originalData?.productName),
      flex: 1
    },
    {
      field: 'productId',
      headerName: 'NHI Item Id',
      width: 150,
      cellRenderer: (params) => params.data.productId === -1 ? "Added By Invoice" : params.data.productId,
    },
    {
      field: 'uomType',
      headerName: 'UOM Type',
      cellRenderer: (params) => OriginalValueToolTip(params.data.productId < 0, params.data.newData, params.data.newData?.uomType, params.data.originalData?.uomType),
      width: 100
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 100,
      cellRenderer: (params) => <Box display="inline-flex">
        {OriginalValueToolTip(
          params.data.productId < 0,
          params.data.newData,
          params.data.newData?.price?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
          params.data.originalData?.price?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
          params.data.originalData?.isOriginalCartPrice, "price")}
        {resendOrder ? (
          <CurrencyInput sx={{ "& .MuiOutlinedInput-root": { height: "34px" }, width: "100px", marginTop: "3px" }} decimalScale={2} name="price"
            size="small" customInput={TextField} value={params.node.data.newData?.price ?? 0}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
            onValueChange={(value) => {
              if ((Number(value) !== params.data.newData?.price && !Number.isNaN(Number(value)))) {
                updateRow(params, "price", value ?? 0)
              }
            }}
            onBlur={() => {
              if (params.node.data.newData?.price !== undefined) {
                updateOrderItems(params, "price", params.node.data.newData?.price)
              }
            }}
          />
        ) : null} </Box>
    },
    {
      field: 'vendorPrice',
      headerName: 'Vendor Price',
      width: 100,
      cellRenderer: (params) => <Box display="inline-flex">
        {OriginalValueToolTip(
          params.data.productId < 0,
          params.data.newData,
          params.data.newData?.vendorPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
          params.data.originalData?.vendorPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
          params.data.originalData?.isOriginalCartPrice, "vendorPrice")}
        {resendOrder ? (
          <CurrencyInput sx={{ "& .MuiOutlinedInput-root": { height: "34px" }, width: "100px", marginTop: "3px" }} decimalScale={2} name="vendorPrice"
            size="small" customInput={TextField} value={params.node.data.newData?.vendorPrice ?? 0}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
            onValueChange={(value) => {
              if ((Number(value) !== params.node.data.newData?.vendorPrice && !Number.isNaN(Number(value)))) {
                updateRow(params, "vendorPrice", value ?? 0);
              }
            }}
            onBlur={() => {
              if (params.node.data.newData?.vendorPrice !== undefined) {
                updateOrderItems(params, "vendorPrice", params.node.data.newData?.vendorPrice)
              }
            }}
          />
        ) : null} </Box>
    },
    {
      field: 'vendorItemId',
      headerName: 'Vendor Item Id',
      cellRenderer: (params) => OriginalValueToolTip(params.data.productId < 0, params.data.newData, params.data.newData?.vendorItemId, params.data.originalData?.vendorItemId),
      width: 150,
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      cellRenderer: (params) => OriginalValueToolTip(params.data.productId < 0, params.data.newData, params.data.newData?.vendor, params.data.originalData?.vendor),
      width: 150,
    },
    ...(resendOrder ? [
      {
        field: 'quantity',
        headerName: 'Quantity',
        width: 80,
        cellRenderer: (params) => <TextField size="small" sx={{ "& .MuiOutlinedInput-root": { height: "34px" }, marginTop: "3px", width: 80 }}
          value={params.data.quantity ?? 0} onChange={(e) => {
            let val = e.target.value?.replace(/[^0-9]/g, '') ?? 0;
            if (val !== "0") val = val.replace(/^0+/, '');
            updateRow(params, "quantity", val)
            updateOrderItems(params, "quantity", val)
          }} />
      },
      {
        field: 'resend',
        headerName: 'Resend',
        width: 150,
        cellRenderer: (params) => <Checkbox checked={params.data.resend ?? false} onChange={(e) => {
          updateRow(params, "resend", e.target.checked)
          updateOrderItems(params, "resend", e.target.checked)
        }} />,
        headerComponent: (params) => AGGridHeaderCheckboxSimple(params, updateAllRowResends),
      },
    ] : [{
      field: 'quantity',
      headerName: 'Quantity',
      minWidth: 150,
      cellRenderer: (params) => <Box display="flex" alignItems="center">
        <Box sx={{ mr: 1 }}>{params.data.quantity}</Box>
        {params.data.trackingData?.[0]?.trackingNumber != null && <Tooltip title={<Box>
          {params.data.trackingData.map((track, index) => <Box key={index}>
            <Box> {track.isManual ? 'Manual Invoice' : `Amount Shipped: ${track.amountShipped} / ${track.uomType}`} </Box>
            {track.carrier && <Box> {`Carrier: ${track.carrier}`} </Box>}
            <Box onClick={() => copyToClipboard(track.trackingNumber)}>
              Tracking Number: {track.trackingNumber}
              <ContentCopy sx={{ color: 'secondary.main', fontSize: 14, cursor: "pointer", mx: .5, mb: "-2px" }} />
            </Box>
          </Box>)}
        </Box>} arrow placement="top">
          <IconButton edge="end" color="secondary" disableRipple sx={{ px: 0, my: 0, mr: 1 }}
            onClick={() => copyToClipboard(params.data.trackingData.map(tdata => tdata.trackingNumber).join(", "))}>
            <LocalShipping />
          </IconButton>
        </Tooltip>}
        {params.data.invoiceData && params.data.invoiceData.length > 0 && <Tooltip title={<Stack spacing={1}>
          {params.data.invoiceData.map((invoice, index) => <Box key={index}>
            <Box>Amount Invoiced: {invoice.price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} / {invoice.quantity} / {invoice.uomType}</Box>
            <Box>Description: {invoice.productName}</Box>
            <Box>Invoice: {invoice.invoiceId}</Box>
          </Box>)}
        </Stack>} arrow placement="top">
          <IconButton edge="end" color="secondary" disableRipple sx={{ px: 0, my: 0, mr: 1 }}>
            <ReceiptLong />
          </IconButton>
        </Tooltip>}
      </Box>
    }])
  ], [resendOrder]);

  return <Dialog open={open} onClose={close} maxWidth="xl" fullWidth>
    <DialogContent>
      {!loading ? <Stack spacing={1}>
        <Box>
          <AGGridQuickFilterWrapper gridRef={gridRef}>
            <Box>
              <Typography variant="subtitle1">Facility</Typography>
              <Box>{facility}</Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "end" }}>
              <Button variant="contained" startIcon={resendOrder ? <CloseIcon /> : <Iconify icon="eva:plus-fill" />}
                onClick={() => {
                  if (!facilityOnHold) {
                    if (resendOrder) { setOrderItems(JSON.parse(JSON.stringify(originalOrderItems))); }
                    setResendOrder(!resendOrder);
                  }
                  else enqueueSnackbar("You can't resend the order while the facility is on hold", { variant: 'error' })
                }}
                color={resendOrder ? "error" : "primary"} size="small">
                <Typography>{resendOrder ? "Cancel " : ""}Resend Order</Typography>
              </Button>
            </Box>
            {!resendOrder && isInRole("Admin") && <Box sx={{ display: "flex", alignItems: "end" }}>
              <Button variant="contained" startIcon={<FileDownload />} onClick={() => setOpenOrderFiles(true)} color={"primary"} size="small">
                <Typography>Order Files</Typography>
              </Button>
            </Box>}
            {!resendOrder && isInRole("Admin") && <Box sx={{ display: "flex", alignItems: "end" }}>
              <Tooltip arrow title=" Restricts electronic invoices from sending out to the customer. Only manual invoices can be sent for this order.">
                <Typography sx={{ mb: -1, ml: -1 }}>
                  <Checkbox disabled={submittingRestrictCustomerInvoices} disableRipple checked={restrictCustomerInvoices} onChange={(e) => handleRestrictCustomerInvoices(e.target.checked)} />
                  Restrict Customer Invoices
                </Typography>
              </Tooltip>
            </Box>}
            {resendOrder && <Box sx={{ display: "flex", alignItems: "end" }}>
              <ConfirmDialog
                button="contained"
                icon="eva:plus-fill"
                color="primary"
                action={resubmitOrder}
                disabled={disableReorderButton}
                actionButton="Submit Order"
                title="Are you sure you want to resend this order?"
                content="If the order is resent the facility may receive the selected items twice."
              />
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={resendOrderAsNewOrder} sx={{ py: .5 }} disableRipple onChange={(e) => setResendOrderAsNewOrder(e.target.checked)} />
                Send with new order number
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={resendWithConfirmationEmail} sx={{ py: .5 }} disableRipple onChange={(e) => setResendWithConfirmationEmail(e.target.checked)} />
                Send with confirmation email
              </Box>
            </Box>}
          </AGGridQuickFilterWrapper>
          <Box sx={{ height: "500px" }}>
            <AgGridReact
              ref={gridRef}
              {...AgGridDefaults}
              columnDefs={columns}
              rowData={orderItems}
              onFilterChanged={(params) => params.api.refreshHeader()}
              onNewColumnsLoaded={autoSizeColumns}
            />
          </Box>
        </Box>
      </Stack> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      <OrderFiles open={openOrderFiles} close={() => setOpenOrderFiles(false)} editOrder={editOrder} />
    </DialogContent>
  </Dialog>;
}
